import { useMemo } from "react";

import { DateTime } from "luxon";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

import usePartDetails from "../../../data/usePartDetails";
import useWarehouseLocations from "../../../data/useWarehouseLocations";
import { IPart, IPartCategory } from "../../../models/Part";
import formatDateTime from "../../../utils/formatDateTime";
import openInNewTab from "../../../utils/openInNewTab";
import ClickToCopy from "../../shared/ClickToCopy";
import PartNotesIndicator from "../../shared/PartNotesIndicator";
import EbayLinkButton from "../../shared/Inputs/EbayLinkButton";
import PartGradeBadge from "../../shared/PartGradeBadge";
import PartStatusLabel from "../../ebayOrders/PartStatusLabel";
import StockMpnCell from "../../stock/StockMpnCell";

export default function UnlistedPartTypeCell({
  part,
  categories,
}: {
  part: IPart;
  categories: IPartCategory[] | undefined;
}) {
  // Paged `/part/listings` rows often omit note arrays; merge details so the
  // indicator matches the expanded row (same React Query key as usePartDetails there).
  const listIncludesNoteArrays =
    part.notes !== undefined && part.internalNotes !== undefined;
  const { partDetails } = usePartDetails(part.id, !listIncludesNoteArrays);
  const { warehouseLocations } = useWarehouseLocations();

  const partForNotes = useMemo(() => {
    if (listIncludesNoteArrays) return part;
    if (!partDetails) return part;
    return {
      ...part,
      notes: partDetails.notes ?? part.notes ?? [],
      internalNotes: partDetails.internalNotes ?? part.internalNotes ?? [],
    };
  }, [listIncludesNoteArrays, part, partDetails]);

  const category = useMemo(
    () => categories?.find((cat) => cat.id === part.categoryId),
    [part, categories]
  );

  const makeModelString = useMemo(() => {
    let string = "";

    if (part.vehicle?.dvlaYearOfManufacture) {
      string = `${part.vehicle.dvlaYearOfManufacture}+`;
    }

    string += part.vehicle?.combinedMake ?? "";
    string += "+";
    string += part.vehicle?.modelRangeDescription ?? "";

    if (part.category && part.category.name) {
      string += "+";
      string += part.category.name;
    }

    return string;
  }, [part]);

  const name = category?.name ?? part.category?.name ?? "—";

  const locationLabel = useMemo(() => {
    const embedded = part.warehouseLocation?.displayName;
    if (embedded) return embedded;
    if (!part.warehouseLocationId) return null;
    const fromList = warehouseLocations.data?.data.find(
      (l) => l.id === part.warehouseLocationId
    )?.displayName;
    return fromList ?? null;
  }, [part.warehouseLocation, part.warehouseLocationId, warehouseLocations.data?.data]);

  const daysOld = useMemo(() => {
    if (!part.created) return null;
    const dt = DateTime.fromISO(part.created);
    if (!dt.isValid) return null;
    const d = Math.floor(DateTime.now().diff(dt, "days").days);
    return d < 0 ? 0 : d;
  }, [part.created]);

  const daysOldChipClass =
    daysOld == null
      ? ""
      : daysOld >= 90
        ? "bg-red-100 text-red-800 ring-1 ring-inset ring-red-200/80"
        : daysOld >= 30
          ? "bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200/80"
          : "bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200/80";

  return (
    <div
      className="flex h-full min-w-0 flex-col gap-2.5 text-left"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex h-full flex-col rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white px-3 py-2.5 shadow-sm ring-1 ring-slate-200/50">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Part information
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openInNewTab(`/stock/${part.id}`);
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
            aria-label="Open part in a new tab"
            title="Open part in a new tab"
          >
            View
            <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
          </button>
        </div>
        <div className="flex min-h-0 min-w-0 flex-col gap-1.5">
          <div className="min-w-0 font-medium text-slate-800">{name}</div>
          <div className="text-xs text-slate-600">
            <span className="text-slate-500">Tag </span>
            {part.tagNumber ? (
              <ClickToCopy text={part.tagNumber} />
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </div>
          <div className="text-xs text-slate-600">
            <span className="text-slate-500">Location </span>
            {locationLabel ? (
              <span className="text-slate-800">{locationLabel}</span>
            ) : part.warehouseLocationId && warehouseLocations.isLoading ? (
              <span className="text-slate-400">…</span>
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            <span>
              Created {formatDateTime(part.created, DateTime.DATE_SHORT)}
            </span>
            {daysOld != null && (
              <span
                className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-xs ${daysOldChipClass}`}
                title="Days since this part was created in Wilma"
              >
                {daysOld}d old
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 border-t border-slate-200 pt-1.5">
            <PartStatusLabel part={part} compact />
            <span className="shrink-0">
              <PartGradeBadge part={part} />
            </span>
            <span className="inline-flex shrink-0 items-center">
              <PartNotesIndicator part={partForNotes} splitIndicators />
            </span>
            {category ? <EbayLinkButton query={makeModelString} /> : null}
          </div>
        </div>

        <div className="mt-2.5 border-t border-slate-200/80 pt-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            MPN &amp; part numbers
          </p>
          <div className="-mx-0.5">
            <StockMpnCell part={part} />
          </div>
        </div>
      </div>
    </div>
  );
}
